/**
 * Inspirasi F1 — the one status language every surface speaks (Herdr's five semantic states,
 * rolled up from task/run/checkpoint/review rather than from a terminal screen).
 * Client-safe: no server imports.
 *
 *   blocked  → something is waiting for the user (approve, answer, review, retry)
 *   working  → an agent / job is busy on the user's behalf
 *   done     → work finished since the user last looked at the workspace
 *   idle     → nothing to do right now
 *   unknown  → not enough data (no plan yet, or the request failed)
 */
export type AttentionState = 'blocked' | 'working' | 'done' | 'idle' | 'unknown'

/** Ordered by urgency — used for the roll-up (the most urgent state wins). */
export const ATTENTION_PRIORITY: readonly AttentionState[] = ['blocked', 'working', 'done', 'idle', 'unknown']

/** Combine several states into the one that matters most (Herdr: one blocked agent makes the workspace blocked). */
export function rollUpAttention(states: readonly AttentionState[]): AttentionState {
  if (!states.length) return 'unknown'
  for (const s of ATTENTION_PRIORITY) if (states.includes(s)) return s
  return 'unknown'
}

/**
 * Orkestrator E2 — one agent's effective state (`runner/state.ts`) in the attention language:
 * waiting on the user → blocked, busy → working, finished → done (or idle when nothing new came
 * out of it), silent → unknown. Anything unrecognised is unknown too.
 */
export function attentionFromAgentState(state: string | null | undefined, opts: { finished?: boolean } = {}): AttentionState {
  switch (state) {
    case 'blocked':
    case 'waiting_input':
      return 'blocked'
    case 'active':
      return 'working'
    case 'idle':
    case 'exited':
      return opts.finished ? 'done' : 'idle'
    default:
      return 'unknown'
  }
}

/** Orkestrator E1 — items derived from the latest `workflow_run` of a plan (agent teams); E2 adds `workflow_review` (reviewer gate). */
export type WorkflowInboxKind = 'workflow_conflict' | 'workflow_checkpoint' | 'workflow_needs_reconciliation' | 'workflow_failed' | 'workflow_done' | 'workflow_review'
/** Orkestrator E2 — `approval` (an agent waits for "allow?") and `decision_card` ("choose a direction"). */
export type InboxKind = 'checkpoint' | 'run_escalated' | 'proposal' | 'review_request' | 'task_failed' | 'comment' | 'deploy' | WorkflowInboxKind | 'approval' | 'decision_card'

/** Orkestrator E2 — the reviewer-gate error the workflow runtime writes on a failed team. */
export const REVIEW_GATE = 'review_gate'
/** Open reviewer findings per severity (`verification_comment` rows with status `open`). */
export interface ReviewCounts {
  critical: number
  major: number
  minor: number
}

export interface InboxItemDto {
  /** stable id: `<kind>:<row id>` */
  id: string
  kind: InboxKind
  state: AttentionState
  /** short human title (plain words in simple mode) */
  title: string
  /** the evidence behind the state — Herdr `agent explain` for people; a few lines, never a log dump */
  explain: string
  /** where the primary action lives */
  href: string
  /** machine-readable reason behind a workflow item (`budget_usd`, `merge_conflict`, `lease_expired`, …) — the UI maps it to plain words; absent on the 7 classic kinds */
  code?: string
  /* ---- Orkestrator E2 (additive; present only on their own kind) ---- */
  /** `approval`: the request to answer with POST /api/approvals/:approvalId */
  approvalId?: string
  /** `approval`: what the agent wants to do */
  payload?: { tool: string | null; command: string | null; path: string | null; reason: string | null }
  /** `approval`: when the waiting agent gives up (ISO) */
  expiresAt?: string
  /** `approval`: row status (always `pending` while listed) */
  status?: string
  /** Orkestrator E4 (additive): `approval` — the task of the run/session that asked (null when unknown); the Kanban "Giliran" lens uses it */
  taskId?: string | null
  /** `workflow_review`: the team whose reviewer gate stopped it */
  workflowId?: string
  /** `workflow_review`: open findings per severity */
  counts?: ReviewCounts
  /**
   * Orkestrator E3 §3: `workflow_failed` stopped by `rate_limit` / `machine_offline` — the owner MAY continue
   * the work elsewhere (POST /api/plan/:planId/workflows/continue-elsewhere with confirm). Never automatic.
   */
  actions?: (InboxAction | UndoLastStepAction)[]
  /**
   * `decision_card`: the card to choose with POST /api/plan/:planId/decision-cards/:cardId/choose.
   * Orkestrator E3 (E2 carry-over §9.3): a `workflow_conflict` / `workflow_review` item carries its own open
   * conflict/review card here instead of a second `decision_card` item.
   */
  cardId?: string
  question?: string
  options?: { key: string; label: string; consequence: string }[]
  planId: string
  planTitle: string
  workspaceId: string
  workspaceName: string
  createdAt: string
}

/**
 * Orkestrator E4 §4 — `task_failed` whose last agent turn has a checkpoint: "Batalkan langkah terakhir".
 * V1 run → POST /api/run/:runId/undo {turnSeq}; cloud team session → POST /api/workflows/:workflowId/sessions/:sessionId/undo {turnSeq}.
 * `label` / `confirm` / `done` are plain server text in the plan language (the UI asks before calling).
 */
export interface UndoLastStepAction {
  kind: 'undo_last_step'
  runId?: string
  workflowId?: string
  sessionId?: string
  turnSeq: number
  label: string
  confirm: string
  done: string
}

/** Orkestrator E3 §3 — a one-click follow-up an Inbox item offers (the endpoint still asks for confirmation). */
export interface InboxAction {
  kind: 'continue_elsewhere'
  executor: 'cloud' | 'local'
  /** estimated USD per step on the new payer (cloud only; absent when no estimate is available) */
  estimateUsd?: number
}

/**
 * Orkestrator E3 (E2 carry-over §9.3): which open decision card merges into which workflow item —
 * a card with source `conflict`/`review` whose `meta.workflowId` names the team; the matching source wins.
 */
export function mergeableCard<C extends { source?: string | null; meta?: Record<string, unknown> | null }>(kind: string, workflowId: string, cards: C[]): C | null {
  if (kind !== 'workflow_conflict' && kind !== 'workflow_review') return null
  const own = cards.filter((c) => (c.source === 'conflict' || c.source === 'review') && c.meta?.workflowId === workflowId)
  return own.find((c) => c.source === (kind === 'workflow_conflict' ? 'conflict' : 'review')) ?? own[0] ?? null
}

export interface InboxDto {
  items: InboxItemDto[]
  counts: { blocked: number; working: number; done: number }
}

export interface WorkspaceAttentionDto {
  workspaceId: string
  state: AttentionState
  /** how many inbox items point at this workspace */
  blocked: number
  /** tasks finished since the user last opened the workspace */
  freshDone: number
}

/** Tailwind classes per state — one pill everywhere, never an accent colour. */
export const ATTENTION_CLASS: Record<AttentionState, string> = {
  blocked: 'bg-status-progress/15 text-status-progress-text',
  working: 'bg-primary/10 text-primary',
  done: 'bg-status-done/15 text-status-done-text',
  idle: 'bg-muted text-muted-foreground',
  unknown: 'bg-muted text-muted-foreground/70 border border-dashed',
}

/* ------------------------------------------------------------------------------------------------
 * Orkestrator E1 — pure helpers that turn a workflow (agent team) into inbox language. Client-safe.
 * The server (`inbox.server.ts`) feeds them rows; the unit test feeds them literals.
 * ---------------------------------------------------------------------------------------------- */

export type BudgetStop = 'budget_tokens' | 'budget_usd' | 'budget_wall'
/** Any `budget_*` code (the server writes `budget_tokens|budget_cost|budget_time`; the contract spells `budget_usd|budget_wall`). */
export const isBudgetError = (error: string | null | undefined): boolean => typeof error === 'string' && error.startsWith('budget_')
/** The NORMALISED budget code when a workflow stopped at one of its own thresholds, else null — both spellings accepted. */
export function budgetStop(error: string | null | undefined): BudgetStop | null {
  switch (error) {
    case 'budget_tokens':
      return 'budget_tokens'
    case 'budget_cost':
    case 'budget_usd':
      return 'budget_usd'
    case 'budget_time':
    case 'budget_wall':
      return 'budget_wall'
    default:
      return null
  }
}

export const WORKFLOW_DONE_WINDOW_MS = 86_400_000
export const WORKFLOW_FAILED_WINDOW_MS = 7 * 86_400_000
type Lang = 'id' | 'en'
const lang = (l: string | null | undefined): Lang => (l === 'id' ? 'id' : 'en')
const ms = (d: Date | string | null | undefined) => (d ? new Date(d).getTime() : NaN)

/**
 * Which inbox kind the latest workflow of a plan yields — null when nothing is waiting.
 * `conflict` = `conflict_task_id` set or a session errored with `merge_conflict` (and the fix task is still open);
 * a `passed` team shows as done for 24 h, a `failed` one stays blocked for 7 days (until the user starts a new team).
 */
export function workflowInboxKind(wf: { status: string; error?: string | null; endedAt?: Date | string | null; createdAt?: Date | string | null }, opts: { conflict?: boolean; now?: number } = {}): WorkflowInboxKind | null {
  const now = opts.now ?? Date.now()
  const ended = ms(wf.endedAt ?? wf.createdAt)
  if (opts.conflict && wf.status !== 'passed' && wf.status !== 'cancelled') return 'workflow_conflict'
  if (wf.status === 'checkpoint') return 'workflow_checkpoint'
  if (wf.status === 'needs_reconciliation') return 'workflow_needs_reconciliation'
  // E2: a team stopped by the reviewer gate is its own kind (same 7-day window as any failed team)
  if (wf.status === 'failed') return !Number.isFinite(ended) || now - ended <= WORKFLOW_FAILED_WINDOW_MS ? (wf.error === REVIEW_GATE ? 'workflow_review' : 'workflow_failed') : null
  if (wf.status === 'passed') return Number.isFinite(ended) && now - ended <= WORKFLOW_DONE_WINDOW_MS ? 'workflow_done' : null
  return null
}

const REASON: Record<Lang, Record<string, string>> = {
  id: {
    budget_tokens: 'Dihentikan karena batas pemakaian',
    budget_usd: 'Dihentikan karena batas biaya',
    budget_wall: 'Dihentikan karena batas waktu',
    merge_conflict: 'Bentrok saat menggabungkan hasil',
    lease_expired: 'Komputer yang mengerjakan berhenti memberi kabar',
    dependency_blocked: 'Bagian ini menunggu bagian lain yang belum selesai',
    context_changed: 'Rencana berubah saat tim bekerja',
    cancel_requested: 'Kamu meminta tim berhenti',
    review_gate: 'Pemeriksa menemukan masalah yang perlu diperbaiki dulu',
    rate_limit: 'Batas pemakaian layanan AI tercapai',
    machine_offline: 'Komputer yang mengerjakan tidak terhubung',
  },
  en: {
    budget_tokens: 'Stopped at the usage limit',
    budget_usd: 'Stopped at the cost limit',
    budget_wall: 'Stopped at the time limit',
    merge_conflict: 'Conflict while merging the results',
    lease_expired: 'The computer doing the work stopped reporting',
    dependency_blocked: 'This part waits for another unfinished part',
    context_changed: 'The plan changed while the team was working',
    cancel_requested: 'You asked the team to stop',
    review_gate: 'The reviewer found issues to fix first',
    rate_limit: 'The AI service usage limit was reached',
    machine_offline: 'The computer doing the work is not connected',
  },
}
/** Plain words for a workflow error code; free-text errors pass through (already sentences), unknown codes stay as-is. */
export function workflowReason(error: string | null | undefined, language?: string | null): string {
  if (!error) return ''
  const code = budgetStop(error) ?? error
  return REASON[lang(language)][code] ?? (isBudgetError(code) ? REASON[lang(language)].budget_tokens! : code.slice(0, 200))
}

/** Title of a workflow inbox item (the line under the kind label). */
export function workflowItemTitle(kind: WorkflowInboxKind, ctx: { error?: string | null; fixTask?: { ref: string; title: string; plainTitle?: string | null } | null; conflictRefs?: string[]; checkpoint?: { phase?: number | null; taskRef?: string | null } | null; parts?: number }, language?: string | null): string {
  const l = lang(language)
  switch (kind) {
    case 'workflow_conflict':
      return ctx.fixTask ? `${ctx.fixTask.ref} · ${ctx.fixTask.plainTitle ?? ctx.fixTask.title}` : ctx.conflictRefs?.length ? ctx.conflictRefs.join(', ') : workflowReason('merge_conflict', l)
    case 'workflow_checkpoint':
      return ctx.checkpoint?.taskRef ?? (ctx.checkpoint?.phase ? String(ctx.checkpoint.phase) : '')
    case 'workflow_needs_reconciliation':
    case 'workflow_failed':
    case 'workflow_review':
      return workflowReason(ctx.error, l)
    case 'workflow_done':
      return ctx.parts ? (l === 'id' ? `${ctx.parts} bagian selesai` : `${ctx.parts} part${ctx.parts === 1 ? '' : 's'} finished`) : ''
  }
}

const EVENT: Record<Lang, Record<string, string>> = {
  id: {
    created: 'Tim dibentuk',
    claimed: 'Komputer kamu mengambil pekerjaan ini',
    wave_ready: 'Bagian berikutnya siap dikerjakan',
    session_started: 'Bagian {ref} mulai dikerjakan',
    session_succeeded: 'Bagian {ref} selesai',
    session_failed: 'Bagian {ref} gagal',
    integrated: 'Hasil digabung dan diuji',
    checkpoint: 'Menunggu persetujuan tahap darimu',
    completed: 'Semua selesai dan terverifikasi',
    cancel_requested: 'Kamu meminta tim berhenti',
    reconciled: 'Semua proses sudah berhenti',
    message: 'Kamu mengirim arahan',
    merge_conflict: 'Bentrok saat menggabungkan hasil {ref}',
    inbox_notified: 'Kamu diberi tahu lewat Perlu Anda',
    payer_changed_by_user: 'Kamu memindahkan pekerjaan ke tempat lain',
    other: 'Catatan: {kind}',
  },
  en: {
    created: 'Team formed',
    claimed: 'Your computer picked up the work',
    wave_ready: 'The next parts are ready to start',
    session_started: 'Part {ref} started',
    session_succeeded: 'Part {ref} finished',
    session_failed: 'Part {ref} failed',
    integrated: 'Results merged and tested',
    checkpoint: 'Waiting for your stage approval',
    completed: 'Everything finished and verified',
    cancel_requested: 'You asked the team to stop',
    reconciled: 'Every process has stopped',
    message: 'You sent a message',
    merge_conflict: 'Conflict while merging {ref}',
    inbox_notified: 'You were notified through Needs You',
    payer_changed_by_user: 'You moved the work somewhere else',
    other: 'Note: {kind}',
  },
}
/** One plain sentence per `workflow_event` row (kind + a short payload detail). */
export function describeWorkflowEvent(ev: { kind: string; payload?: Record<string, unknown> | null }, language?: string | null): string {
  const l = lang(language)
  const p = ev.payload ?? {}
  const ref = typeof p.ref === 'string' ? p.ref : Array.isArray(p.refs) ? (p.refs as unknown[]).filter((x): x is string => typeof x === 'string').join(', ') : ''
  const tpl = EVENT[l][ev.kind] ?? EVENT[l].other!
  let out = tpl.replace('{ref}', ref).replace('{kind}', ev.kind).replace(/\s+\(?\)?$/, '').trim()
  if (ev.kind === 'wave_ready' && ref) out += ` (${ref})`
  if (ev.kind === 'session_failed' && typeof p.summary === 'string' && p.summary) out += ` — ${p.summary.slice(0, 120)}`
  return out
}
/** Internal bookkeeping events that never become a sentence for people (`inbox_notified` = notification dedup marker). */
export const INTERNAL_WORKFLOW_EVENTS: ReadonlySet<string> = new Set(['inbox_notified'])
/** `explain` of a workflow item: the last events (internal markers skipped), oldest first, one sentence per line. */
export function explainWorkflow(events: { kind: string; payload?: Record<string, unknown> | null }[], language?: string | null, extra: string[] = []): string {
  return [...events.filter((e) => !INTERNAL_WORKFLOW_EVENTS.has(e.kind)).map((e) => describeWorkflowEvent(e, language)), ...extra].filter(Boolean).join('\n').slice(0, 600)
}

/** Orkestrator E2 — the `explain` line of a `workflow_review` item: open findings per severity. */
export function reviewCountsLine(counts: ReviewCounts, language?: string | null): string {
  const l = lang(language)
  const words = l === 'id' ? { critical: 'kritis', major: 'mayor', minor: 'minor' } : { critical: 'critical', major: 'major', minor: 'minor' }
  const parts = (['critical', 'major', 'minor'] as const).filter((k) => counts[k] > 0).map((k) => `${counts[k]} ${words[k]}`)
  if (!parts.length) return l === 'id' ? 'Tidak ada temuan terbuka' : 'No open findings'
  return `${l === 'id' ? 'Temuan terbuka' : 'Open findings'}: ${parts.join(' · ')}`
}
